<template>
  <div class="accounts-mobile-field">
    <collapse-height
      v-if="!fieldInstance.fieldData.is_hidden"
      ref="collapse"
      @toggle-collapse="$emit('toggle-collapse', $event, 0); isOpened = $event">
      <template #toggler="{ isOpened }">
        <div class="accounts-mobile-field__header">
          <div class="accounts-mobile-field__header-title">
            {{ fieldInstance.fieldData.placeholder || 'Search items' }}
          </div>

          <div class="accounts-mobile-field__header-info">
            <arrowhead-icon
              class="accounts-mobile-field__header-arrow"
              :is-reverse="isOpened" />
          </div>
        </div>
      </template>

      <template #content>
        <component
          :is="componentMap.get(fieldInstance.field.component)"
          v-bind="componentProps"
          @search="search"
          @[fieldInstance.field.emit]="emit" />
      </template>
    </collapse-height>

    <div
      v-if="dropdownRangeProps"
      class="accounts-field__item">
      <collapse-height
        ref="collapseRange"
        @toggle-collapse="$emit('toggle-collapse', $event, 1); isOpened = $event">
        <template #toggler="{ isOpened }">
          <div class="accounts-mobile-field__header">
            <div class="accounts-mobile-field__header-title">
              {{ dropdownRangeProps.placeholder }}
            </div>

            <div class="accounts-mobile-field__header-info">
              <arrowhead-icon
                class="accounts-mobile-field__header-arrow"
                :is-reverse="isOpened" />
            </div>
          </div>
        </template>

        <template #content>
          <accounts-mobile-fields-range
            v-bind="dropdownRangeProps"
            @input="handleDropdownRangeInput" />
        </template>
      </collapse-height>
    </div>
  </div>
</template>

<script>
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import CollapseHeight from 'Components/ui/CollapseHeight/CollapseHeight.vue';

export default {
  name: 'AccountsMobileField',
  components: {
    CollapseHeight,
    ArrowheadIcon,
    AccountsMobileFieldsMultipleSearchInput:
      () => import('Components/Accounts/Mobile/Fields/AccountsMobileFieldsMultipleSearchInput.vue'),
    AccountsMobileFieldsRange:
      () => import('Components/Accounts/Mobile/Fields/AccountsMobileFieldsRange.vue'),
  },

  props: {
    fieldInstance: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      componentMap: new Map([
        ['multiple-search-input', 'accounts-mobile-fields-multiple-search-input'],
        ['dropdown-range', 'accounts-mobile-fields-range'],
      ]),
      isOpened: false,
    };
  },

  computed: {
    componentProps() {
      return {
        ...this.fieldInstance.field.props,
        [this.fieldInstance.field.valueKey]: this.fieldInstance.field.value,
        disabled: this.isDisabled || this.fieldInstance.field.props.disabled,
        error: this.errorText,
        // SKY-5994: For has_custom_value field type, allow to select only one value
        ...(this.fieldInstance.field.component === 'multiple-search-input'
        && this.fieldInstance.fieldData.has_custom_value
          ? { 'is-radio': true } : {}),
        isOpened: this.isOpened,
      };
    },

    dropdownRangeProps() {
      if (!this.fieldInstance.fieldData.has_values_counter) {
        return null;
      }

      const maximumValue = this.fieldInstance.fieldData.product_currency_setting_field_values.length;
      const values = this.fieldInstance.fieldData.default_range_value
        ? this.fieldInstance.fieldData.default_range_value
        : [1, maximumValue];

      return {
        placeholder: this.fieldInstance.fieldData.component_view_data.values_counter_placeholder
          || `${this.fieldInstance.fieldData.placeholder}s count`,
        range: {
          values,
          borderValues: [1, maximumValue],
          step: 1,
          zones: [
            {
              from: 1,
              to: maximumValue,
              size: 100,
              step: 1,
            },
          ],
          scaleStepsCount: 1,
        },
        isOpened: this.isOpened,
      };
    },
  },

  methods: {
    emit(event) {
      if (this.fieldInstance.field.props.type === 'number') {
        event = Number(event);
      }

      if (JSON.stringify(this.fieldInstance.field.value) === JSON.stringify(event)) {
        return;
      }
      this.fieldInstance.setValue(event);
      this.$emit('input', {
        field: this.fieldInstance,
        value: event,
      });
    },

    search(event) {
      if (typeof this.fieldInstance.search === 'function') {
        this.fieldInstance.search(event);
      }
    },

    handleDropdownRangeInput(event) {
      this.$emit('input', {
        field: this.fieldInstance,
        range: event,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.accounts-mobile-field__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  color: $color--basic-white;
  border-bottom: 1px solid $color--basic-menu;
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
}

.accounts-mobile-field__header-arrow {
  width: 16px;
  height: 16px;
}

/deep/ .collapse-height__content {
  padding: 12px 0;
}
</style>
